// Карточка тренировки: длительность, объём, итог для персонажа (опыт и мышцы на фигуре), подходы
// по упражнениям с отметками рекордов; внизу у завершённой — разбор ИИ.

import { useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'

import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, Modal, PageSpinner, Stat, useToast } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDayTitle, formatDuration, formatNumber, formatTime } from '@/lib/format'
import { toIsoDate } from '@/lib/periods'
import { SessionGainCard } from '@/pages/character/SessionGainCard'
import { WorkoutRecords } from '@/pages/character/WorkoutRecords'
import { SessionActivityBlock } from '@/pages/metrics-garmin/SessionActivityBlock'

import { useDeleteSession, useSession, type PrFlags } from './api'
import { PR_LABELS, formatSet, groupSets } from './logic'
import { SessionReviewCard } from './SessionReviewCard'
import { TemplateSyncCard } from './TemplateSyncCard'

function PrBadge({ flags }: { flags: PrFlags | null }) {
  if (!flags) return null
  const names = (Object.keys(flags) as (keyof PrFlags)[]).filter((k) => flags[k]).map((k) => PR_LABELS[k])
  if (names.length === 0) return null
  return <Badge tone="warning">рекорд: {names.join(', ')}</Badge>
}

export function SessionPage() {
  const { id = '' } = useParams()
  const session = useSession(id)
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const remove = useDeleteSession()
  const [confirming, setConfirming] = useState(false)

  if (session.isPending) return <PageSpinner />
  if (session.error) {
    return (
      <>
        <PageHeader title="Тренировка" back={{ to: '/workouts', label: 'Тренировки' }} />
        <Card>
          <EmptyState icon="alert" title="Не удалось открыть тренировку" description={<ErrorText error={session.error} />} />
        </Card>
      </>
    )
  }
  const s = session.data
  const title = formatDayTitle(s.started_at)
  const groups = groupSets(s)

  return (
    <>
      <PageHeader
        title={title.charAt(0).toUpperCase() + title.slice(1)}
        back={{ to: '/workouts', label: 'Тренировки' }}
        description={
          <>
            {s.template_name ?? 'Без шаблона'} · {formatTime(s.started_at)}
            {s.ended_at ? `–${formatTime(s.ended_at)}` : ' · идёт'}
            {s.auto_closed ? ' · закрыта автоматически' : ''}
          </>
        }
        actions={<Button onClick={() => navigate(`/workouts/${s.id}/edit`)}>Изменить</Button>}
      />
      <Card>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Stat label="Длительность" value={formatDuration(s.duration_s)} />
          <Stat label="Объём" value={formatNumber(s.volume_kg, { digits: 0 })} unit="кг" />
          <Stat label="Подходов" value={s.sets_done} hint="рабочих, выполненных" />
        </div>
      </Card>
      {s.ended_at ? <SessionGainCard sessionId={s.id} className="mt-4" celebrate={location.state?.celebrate === true} /> : null}
      {s.ended_at ? <WorkoutRecords session={s} /> : null}
      {s.ended_at && groups.length > 0 ? <TemplateSyncCard session={s} /> : null}
      <SessionActivityBlock sessionId={s.id} day={toIsoDate(new Date(s.started_at))} />
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {groups.length === 0 ? (
          <Card>
            <EmptyState icon="workout" title="Подходов нет" />
          </Card>
        ) : (
          groups.map((g) => (
            <Card
              key={`${g.exercise_id}:${g.position}`}
              title={
                <Link to={`/exercises/${g.exercise_id}`} className="hit-area hover:text-accent">
                  {g.name}
                </Link>
              }
              actions={g.archived ? <Badge>в архиве</Badge> : null}
            >
              <ol className="space-y-1.5">
                {g.sets.map((set, i) => (
                  <li
                    key={set.id}
                    className={cn('flex flex-wrap items-center gap-2 text-sm tabular-nums', !set.completed && 'text-muted')}
                  >
                    <span className="w-5 text-muted">{i + 1}</span>
                    <span className={set.completed ? 'text-fg' : 'line-through'}>{formatSet(set, g.tracking)}</span>
                    {set.is_warmup ? <Badge>разминка</Badge> : null}
                    {!set.completed ? <Badge>не выполнен</Badge> : null}
                    <PrBadge flags={set.is_pr} />
                  </li>
                ))}
              </ol>
            </Card>
          ))
        )}
      </div>
      {s.notes ? (
        <Card title="Заметка" className="mt-4">
          <p className="whitespace-pre-line text-sm text-fg">{s.notes}</p>
        </Card>
      ) : null}
      {s.ended_at ? <SessionReviewCard sessionId={s.id} /> : null}
      {/* Удаление — отдельно и внизу, подальше от «Изменить». */}
      <div className="mt-6 flex justify-center">
        <Button variant="ghost" className="text-danger" onClick={() => setConfirming(true)}>
          Удалить тренировку
        </Button>
      </div>
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Удалить тренировку?"
        description="Вместе с подходами. Удаление можно отменить в журнале изменений."
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Отмена</Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() =>
                remove.mutate(s.id, {
                  onSuccess: () => {
                    toast.show({ message: 'Тренировка удалена' })
                    navigate('/workouts', { replace: true })
                  },
                })
              }
            >
              Удалить
            </Button>
          </>
        }
      >
        <ErrorText error={remove.error} />
      </Modal>
    </>
  )
}
